import { useState } from "react";

// ── Formspree endpoint ───────────────────────────────────────────────────────
const FORMSPREE_ID = "xwvrdovk";

// ── Brand question definitions ───────────────────────────────────────────────
const brandQuestions = [
  {
    section: "The Business",
    subtitle: "Tell us the basics",
    questions: [
      { id: "business_name_desc", q: "What is this business called, and what does it do?", hint: "A brief description is perfect", type: "textarea" },
      { id: "location_reach", q: "Where is it located, and does it serve customers locally, regionally, or online?", hint: "", type: "textarea" },
      { id: "ideal_customer", q: "Who is your ideal customer?", hint: "Age range, lifestyle, income level, interests — be as specific as you can", type: "textarea" },
      { id: "differentiator", q: "What sets this business apart from competitors?", hint: "The one thing you'd want a potential customer to know", type: "textarea" },
      {
        id: "brand_tone", q: "What tone or personality best describes this brand?", hint: "Select all that apply",
        type: "checkboxes",
        options: ["Professional", "Casual & Friendly", "Fun & Playful", "Inspirational", "Educational", "Luxury / Premium", "Down-to-earth", "Bold & Energetic", "Warm & Personal", "Other"]
      },
    ]
  },
  {
    section: "Goals & Strategy",
    subtitle: "What success looks like",
    questions: [
      {
        id: "primary_goal", q: "What does success look like for this business in 90 days?", hint: "Choose your primary goal",
        type: "radio",
        options: ["More foot traffic / in-person visits", "Online sales or bookings", "Brand awareness & visibility", "Lead generation", "Community & engagement growth", "All of the above"]
      },
      { id: "lead_offer", q: "Is there a specific product, service, or offer you want to lead with?", hint: "", type: "textarea" },
      {
        id: "starting_point", q: "Are you starting social from scratch, or re-engaging an existing audience?", hint: "",
        type: "radio",
        options: ["Starting from scratch", "Have accounts — inactive or underused", "Active presence — needs improvement", "Not sure"]
      },
      {
        id: "platforms", q: "Which platforms do you want to prioritize?", hint: "Select all that apply",
        type: "checkboxes",
        options: ["Instagram", "Facebook", "TikTok", "Pinterest", "LinkedIn", "YouTube Shorts", "X / Twitter", "Google Business"]
      },
    ]
  },
  {
    section: "Brand Identity",
    subtitle: "Visual & messaging foundation",
    questions: [
      {
        id: "brand_assets", q: "Does this business have an existing logo and brand guidelines?", hint: "",
        type: "radio",
        options: ["Yes — fully developed", "Partial — logo only", "No — needs development", "Not sure"]
      },
      { id: "website", q: "Do you have a website for this business?", hint: "If yes, please share the URL", type: "textarea" },
      { id: "visual_style", q: "Are there any brand colors, fonts, or visual styles already in use?", hint: "e.g. Navy and gold, modern minimalist, rustic and warm", type: "textarea" },
      { id: "taglines", q: "Are there any existing taglines, slogans, or key phrases you want consistently used?", hint: "", type: "textarea" },
      { id: "avoid", q: "Are there any topics, competitors, or messaging you absolutely want to avoid?", hint: "This helps us stay on brand and out of trouble", type: "textarea" },
    ]
  }
];

const sharedQuestions = [
  { id: "approvals_contact", q: "Who is the main point of contact for content approvals?", hint: "Is it the same person for all three businesses, or different people?", type: "textarea" },
  {
    id: "feedback_speed", q: "How quickly can you typically turn around feedback on draft content?", hint: "",
    type: "radio", options: ["Same day", "24–48 hours", "3–5 days", "I'll let you know"]
  },
  {
    id: "content_library", q: "Do you have an existing content library? (photos, videos, logos, brand assets)", hint: "",
    type: "radio", options: ["Yes — lots of it", "Some — it's limited", "Starting from scratch"]
  },
  { id: "content_creation_scope", q: "Will you be providing on-location photos/video, or is content creation part of the scope?", hint: "e.g. I can supply phone photos — I'd need you to handle designed graphics and reels", type: "textarea" },
  {
    id: "paid_budget", q: "Is there a budget for paid social (boosted posts, ads) alongside organic content?", hint: "",
    type: "radio", options: ["Yes — I have a monthly ad budget", "Possibly — open to discussing", "No — organic only for now"]
  },
  { id: "upcoming_events", q: "Are there any upcoming events, promotions, or launches in the next 90 days I should know about?", hint: "e.g. Summer sale in July, grand opening of second location in August...", type: "textarea" },
  { id: "anything_else", q: "Is there anything else you'd like me to know before we get started?", hint: "Anything at all — the more context, the better.", type: "textarea", tall: true },
];

const BRAND_LABELS = ["One", "Two", "Three"];

// ── Styles (scoped) ──────────────────────────────────────────────────────────
const S = {
  page: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    background: "#FAF8F5",
    minHeight: "100vh",
    color: "#3a3240",
  },
  masthead: {
    background: "#3D2B6B",
    padding: "56px 40px 48px",
    position: "relative",
    overflow: "hidden",
  },
  mastheadInner: {
    maxWidth: 860,
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 24,
    flexWrap: "wrap",
  },
  brandLabel: {
    display: "block",
    fontSize: 10,
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    color: "#C9A96E",
    marginBottom: 12,
    fontFamily: "monospace",
  },
  brandName: {
    fontSize: "clamp(28px, 5vw, 44px)",
    fontWeight: 300,
    color: "white",
    lineHeight: 1.15,
    margin: 0,
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  goldRule: {
    height: 1,
    background: "linear-gradient(90deg, #C9A96E 0%, transparent 100%)",
    opacity: 0.4,
    maxWidth: 860,
    margin: "0 auto",
  },
  introStrip: {
    background: "#F0ECF7",
    borderBottom: "1px solid #D8CFF0",
    padding: "24px 40px",
  },
  introInner: {
    maxWidth: 860,
    margin: "0 auto",
    display: "flex",
    gap: 32,
    flexWrap: "wrap",
  },
  introItem: { display: "flex", alignItems: "flex-start", gap: 12, flex: "1 1 200px" },
  introIcon: {
    width: 32, height: 32, borderRadius: 8,
    background: "#3D2B6B",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 14, flexShrink: 0,
  },
  introStrong: { display: "block", fontSize: 12, fontWeight: 600, color: "#3D2B6B", marginBottom: 2 },
  introSpan: { fontSize: 11, color: "#7a6e8a", lineHeight: 1.4 },
  content: { maxWidth: 860, margin: "0 auto", padding: "48px 40px 80px" },
  clientBar: {
    background: "white", border: "1px solid #D8CFF0", borderRadius: 12,
    padding: "28px 32px", marginBottom: 48,
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20,
  },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 6 },
  fieldGroupFull: { display: "flex", flexDirection: "column", gap: 6, gridColumn: "1 / -1" },
  label: { fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "#6B4FA0" },
  input: {
    fontFamily: "inherit", fontSize: 14, color: "#1c1228",
    background: "#FAF8F5", border: "1px solid #D8CFF0", borderRadius: 8,
    padding: "10px 14px", width: "100%", outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  },
  tabRow: { display: "flex", flexDirection: "row", gap: 0, marginBottom: -1, position: "relative", zIndex: 1, flexWrap: "nowrap" },
  tabBase: {
    padding: "12px 24px", fontSize: 12, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    border: "1px solid #D8CFF0", borderBottom: "none",
    borderRadius: "10px 10px 0 0", cursor: "pointer",
    marginRight: 4, transition: "all 0.2s",
    fontFamily: "inherit",
  },
  tabInactive: { background: "#F0ECF7", color: "#7a6e8a" },
  tabActive: { background: "white", color: "#3D2B6B", fontWeight: 700 },
  panel: {
    background: "white", border: "1px solid #D8CFF0",
    borderRadius: "0 12px 12px 12px", overflow: "hidden", marginBottom: 40,
  },
  sectionHeader: {
    padding: "20px 32px", borderBottom: "1px solid #D8CFF0",
    display: "flex", alignItems: "center", gap: 14,
    background: "linear-gradient(135deg, #3D2B6B 0%, #6B4FA0 100%)",
  },
  sectionNum: {
    fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 300,
    fontStyle: "italic", color: "#E8D5A8", lineHeight: 1, minWidth: 28,
  },
  sectionTitle: { fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 600, color: "white", lineHeight: 1.2 },
  sectionSub: { fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 2, letterSpacing: "0.05em" },
  sectionBody: { padding: "8px 32px 16px" },
  qRow: { padding: "18px 0", borderBottom: "1px solid #F0ECF7" },
  qLabel: { fontSize: 13, fontWeight: 500, color: "#1c1228", lineHeight: 1.4, marginBottom: 4 },
  qHint: { fontSize: 11, color: "#7a6e8a", fontStyle: "italic", marginBottom: 8, lineHeight: 1.4 },
  textarea: {
    fontFamily: "inherit", fontSize: 14, color: "#1c1228",
    background: "#FAF8F5", border: "1px solid #D8CFF0", borderRadius: 8,
    padding: "10px 14px", width: "100%", outline: "none", resize: "vertical",
    lineHeight: 1.5, minHeight: 80, boxSizing: "border-box",
  },
  checkGrid: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 },
  checkItem: (checked) => ({
    display: "flex", alignItems: "center", gap: 8,
    padding: "8px 12px", border: `1.5px solid ${checked ? "#6B4FA0" : "#D8CFF0"}`,
    borderRadius: 8, cursor: "pointer", fontSize: 13,
    background: checked ? "#EDE7F6" : "white",
    color: checked ? "#3D2B6B" : "#3a3240",
    fontWeight: checked ? 500 : 400,
    transition: "all 0.15s", userSelect: "none",
    fontFamily: "inherit",
  }),
  checkBox: (checked) => ({
    width: 16, height: 16, borderRadius: 4,
    border: `1.5px solid ${checked ? "#6B4FA0" : "#D8CFF0"}`,
    background: checked ? "#6B4FA0" : "transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, fontSize: 10, color: "white",
  }),
  pillRow: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 },
  pill: (selected) => ({
    padding: "7px 16px", border: `1.5px solid ${selected ? "#6B4FA0" : "#D8CFF0"}`,
    borderRadius: 100, fontSize: 12, cursor: "pointer",
    background: selected ? "#6B4FA0" : "transparent",
    color: selected ? "white" : "#3a3240",
    fontWeight: selected ? 500 : 400,
    transition: "all 0.15s", userSelect: "none",
    fontFamily: "inherit",
  }),
  sharedCard: {
    background: "white", border: "1px solid #D8CFF0",
    borderRadius: 12, overflow: "hidden", marginBottom: 40,
  },
  submitArea: { textAlign: "center", paddingTop: 16 },
  submitNote: { fontSize: 12, color: "#7a6e8a", marginBottom: 20, fontStyle: "italic" },
  btnSubmit: {
    display: "inline-flex", alignItems: "center", gap: 10,
    background: "#3D2B6B", color: "white",
    fontFamily: "inherit", fontSize: 13, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    padding: "16px 48px", border: "none", borderRadius: 100,
    cursor: "pointer", boxShadow: "0 4px 24px rgba(61,43,107,0.25)",
    transition: "all 0.2s",
  },
  btnDisabled: {
    display: "inline-flex", alignItems: "center", gap: 10,
    background: "#9B7FCC", color: "white",
    fontFamily: "inherit", fontSize: 13, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    padding: "16px 48px", border: "none", borderRadius: 100,
    cursor: "not-allowed", opacity: 0.7,
  },
  successBox: {
    background: "white", border: "1px solid #D8CFF0", borderRadius: 16,
    padding: "60px 40px", textAlign: "center",
  },
  successIcon: {
    width: 64, height: 64,
    background: "linear-gradient(135deg, #3D2B6B, #6B4FA0)",
    borderRadius: "50%", display: "flex", alignItems: "center",
    justifyContent: "center", margin: "0 auto 24px", fontSize: 28, color: "white",
  },
  successH2: {
    fontFamily: "Georgia, serif", fontSize: 32, fontWeight: 300,
    color: "#3D2B6B", marginBottom: 12,
  },
  successP: { fontSize: 15, color: "#7a6e8a", lineHeight: 1.6 },
  footer: {
    textAlign: "center", marginTop: 48, paddingTop: 32,
    borderTop: "1px solid #D8CFF0", fontSize: 11, color: "#7a6e8a",
    fontFamily: "monospace", letterSpacing: "0.05em",
  },
  errorBox: {
    background: "#FFF0F0", border: "1px solid #FFCCCC", borderRadius: 8,
    padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#C0392B",
  },
};

// ── Sub-components ───────────────────────────────────────────────────────────
function CheckItem({ label, checked, onToggle }) {
  return (
    <button type="button" style={S.checkItem(checked)} onClick={onToggle}>
      <span style={S.checkBox(checked)}>{checked ? "✓" : ""}</span>
      {label}
    </button>
  );
}

function RadioPill({ label, selected, onSelect }) {
  return (
    <button type="button" style={S.pill(selected)} onClick={onSelect}>
      {label}
    </button>
  );
}

function QuestionField({ question, brandKey, values, onChange }) {
  const fieldKey = brandKey ? `${brandKey}_${question.id}` : question.id;
  const val = values[fieldKey] || (question.type === "checkboxes" ? [] : "");

  if (question.type === "textarea") {
    return (
      <textarea
        style={{ ...S.textarea, minHeight: question.tall ? 110 : 80 }}
        value={val}
        onChange={e => onChange(fieldKey, e.target.value)}
        placeholder="Your answer..."
      />
    );
  }

  if (question.type === "checkboxes") {
    return (
      <div style={S.checkGrid}>
        {question.options.map(opt => (
          <CheckItem
            key={opt} label={opt}
            checked={Array.isArray(val) && val.includes(opt)}
            onToggle={() => {
              const next = Array.isArray(val) ? [...val] : [];
              const idx = next.indexOf(opt);
              if (idx === -1) next.push(opt); else next.splice(idx, 1);
              onChange(fieldKey, next);
            }}
          />
        ))}
      </div>
    );
  }

  if (question.type === "radio") {
    return (
      <div style={S.pillRow}>
        {question.options.map(opt => (
          <RadioPill
            key={opt} label={opt}
            selected={val === opt}
            onSelect={() => onChange(fieldKey, opt)}
          />
        ))}
      </div>
    );
  }

  return null;
}

function QuestionRow({ question, brandKey, values, onChange, last }) {
  return (
    <div style={{ ...S.qRow, borderBottom: last ? "none" : "1px solid #F0ECF7" }}>
      <div style={S.qLabel}>{question.q}</div>
      {question.hint && <div style={S.qHint}>{question.hint}</div>}
      <QuestionField question={question} brandKey={brandKey} values={values} onChange={onChange} />
    </div>
  );
}

function BrandPanel({ brandIndex, values, onChange }) {
  const label = BRAND_LABELS[brandIndex];
  const bKey = `brand${brandIndex + 1}`;

  return (
    <div style={S.panel}>
      {brandQuestions.map((section, sIdx) => (
        <div key={sIdx}>
          <div style={S.sectionHeader}>
            <span style={S.sectionNum}>{sIdx + 1}</span>
            <div>
              <div style={S.sectionTitle}>Business {label} — {section.section}</div>
              <div style={S.sectionSub}>{section.subtitle}</div>
            </div>
          </div>
          <div style={S.sectionBody}>
            {section.questions.map((q, qIdx) => (
              <QuestionRow
                key={q.id} question={q} brandKey={bKey}
                values={values} onChange={onChange}
                last={qIdx === section.questions.length - 1}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ClientIntake() {
  const [activeTab, setActiveTab] = useState(0);
  const [values, setValues] = useState({});
  const [clientInfo, setClientInfo] = useState({ name: "", email: "", phone: "", location: "", how_heard: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (key, val) => setValues(prev => ({ ...prev, [key]: val }));
  const handleClient = (key, val) => setClientInfo(prev => ({ ...prev, [key]: val }));

  // Flatten form data for Formspree
  const buildPayload = () => {
    const flat = { ...clientInfo };
    // Brand data
    [0, 1, 2].forEach(bi => {
      const bKey = `brand${bi + 1}`;
      brandQuestions.forEach(section => {
        section.questions.forEach(q => {
          const fk = `${bKey}_${q.id}`;
          const v = values[fk];
          flat[`Business ${BRAND_LABELS[bi]} — ${q.id}`] = Array.isArray(v) ? v.join(", ") : (v || "");
        });
      });
    });
    // Shared
    sharedQuestions.forEach(q => {
      const v = values[q.id];
      flat[q.id] = Array.isArray(v) ? v.join(", ") : (v || "");
    });
    return flat;
  };

  const handleSubmit = async () => {
    if (!clientInfo.name || !clientInfo.email) {
      setError("Please fill in your name and email address before submitting.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      if (res.ok) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setError("Something went wrong submitting the form. Please email us directly at hello@novacreativeco.com.");
      }
    } catch {
      setError("Network error. Please email us directly at hello@novacreativeco.com.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Input focus style via ref trick (inline only, no CSS modules)
  const inputStyle = S.input;

  if (submitted) {
    return (
      <div style={S.page}>
        <header style={S.masthead}>
          <div style={S.mastheadInner}>
            <div>
              <span style={S.brandLabel}>Nova Creative Co. — Client Intake</span>
              <h1 style={S.brandName}>Let's get to know<br /><em style={{ fontStyle: "italic", color: "#E8D5A8" }}>your businesses.</em></h1>
            </div>
          </div>
        </header>
        <div style={S.content}>
          <div style={S.successBox}>
            <div style={S.successIcon}>✓</div>
            <h2 style={S.successH2}>Thank you — we're excited to get started.</h2>
            <p style={S.successP}>
              Jennifer will review your intake and be in touch within 1–2 business days<br />
              to schedule your strategy session.
            </p>
          </div>
          <div style={S.footer}>
            Nova Creative Co. &nbsp;·&nbsp; novacreativeco.com &nbsp;·&nbsp; hello@novacreativeco.com &nbsp;·&nbsp; North Central Alabama
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* MASTHEAD */}
      <header style={S.masthead}>
        <div style={S.mastheadInner}>
          <div>
            <span style={S.brandLabel}>Nova Creative Co. — Client Intake</span>
            <h1 style={S.brandName}>Let's get to know<br /><em style={{ fontStyle: "italic", color: "#E8D5A8" }}>your businesses.</em></h1>
          </div>
          <div style={{ textAlign: "right" }}>
            <span style={{ display: "block", fontStyle: "italic", color: "rgba(255,255,255,0.5)", fontSize: 13, marginBottom: 4 }}>Social Media &amp; Marketing</span>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.75)" }}>Strategy Intake Form</span>
          </div>
        </div>
      </header>
      <div style={S.goldRule} />

      {/* INTRO STRIP */}
      <div style={S.introStrip}>
        <div style={S.introInner}>
          {[
            ["⏱", "About 15–20 minutes", "Complete at your own pace — no wrong answers"],
            ["🔒", "Completely confidential", "Your information is only used to shape your strategy"],
            ["✦", "One form, three brands", "Use the tabs below to fill in each business separately"],
          ].map(([icon, title, sub]) => (
            <div key={title} style={S.introItem}>
              <div style={S.introIcon}>{icon}</div>
              <div>
                <strong style={S.introStrong}>{title}</strong>
                <span style={S.introSpan}>{sub}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MAIN */}
      <main style={S.content}>

        {error && <div style={S.errorBox}>{error}</div>}

        {/* CLIENT INFO */}
        <div style={S.clientBar}>
          {[
            { key: "name", label: "Your Name", placeholder: "Full name", type: "text", full: false },
            { key: "email", label: "Email Address", placeholder: "email@example.com", type: "email", full: false },
            { key: "phone", label: "Phone Number", placeholder: "(000) 000-0000", type: "tel", full: false },
            { key: "location", label: "City / Area", placeholder: "e.g. Decatur, AL", type: "text", full: false },
            { key: "how_heard", label: "How did you hear about Nova Creative Co.?", placeholder: "Referral, social media, etc.", type: "text", full: true },
          ].map(f => (
            <div key={f.key} style={f.full ? S.fieldGroupFull : S.fieldGroup}>
              <label style={S.label}>{f.label}</label>
              <input
                type={f.type} style={inputStyle} placeholder={f.placeholder}
                value={clientInfo[f.key]}
                onChange={e => handleClient(f.key, e.target.value)}
              />
            </div>
          ))}
        </div>

        {/* BRAND TABS */}
        <div style={S.tabRow}>
          {BRAND_LABELS.map((label, i) => (
            <button
              key={i} type="button"
              style={{ ...S.tabBase, ...(activeTab === i ? S.tabActive : S.tabInactive) }}
              onClick={() => setActiveTab(i)}
            >
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 18, height: 18, borderRadius: "50%", background: "#6B4FA0",
                color: "white", fontSize: 10, marginRight: 6, fontWeight: 700,
              }}>{i + 1}</span>
              Business {label}
            </button>
          ))}
        </div>

        {/* BRAND PANELS */}
        {[0, 1, 2].map(i => (
          <div key={i} style={{ display: activeTab === i ? "block" : "none" }}>
            <BrandPanel brandIndex={i} values={values} onChange={handleChange} />
          </div>
        ))}

        {/* SHARED SECTION */}
        <div style={S.sharedCard}>
          <div style={S.sectionHeader}>
            <span style={S.sectionNum}>4</span>
            <div>
              <div style={S.sectionTitle}>Logistics &amp; Approvals</div>
              <div style={S.sectionSub}>Applies across all three businesses</div>
            </div>
          </div>
          <div style={S.sectionBody}>
            {sharedQuestions.map((q, i) => (
              <QuestionRow
                key={q.id} question={q} brandKey={null}
                values={values} onChange={handleChange}
                last={i === sharedQuestions.length - 1}
              />
            ))}
          </div>
        </div>

        {/* SUBMIT */}
        <div style={S.submitArea}>
          <p style={S.submitNote}>
            You can also email us directly at hello@novacreativeco.com
          </p>
          <button
            type="button"
            style={submitting ? S.btnDisabled : S.btnSubmit}
            onClick={handleSubmit}
            disabled={submitting}
          >
            <span>{submitting ? "Submitting..." : "Submit Intake Form"}</span>
            {!submitting && <span>→</span>}
          </button>
        </div>

        <div style={S.footer}>
          Nova Creative Co. &nbsp;·&nbsp; novacreativeco.com &nbsp;·&nbsp; hello@novacreativeco.com &nbsp;·&nbsp; North Central Alabama
        </div>
      </main>
    </div>
  );
}
